import React from 'react';
import { Info } from 'lucide-react';
import { CalculatorInputs } from '@/pages/ResourcesBudgetCalculator';

interface Props {
  inputs: CalculatorInputs;
  onInputChange: (field: keyof CalculatorInputs, value: any) => void;
  currency: 'INR' | 'USD';
}



const inputLabel = (label: string, tooltip?: string) => (
  <div className="flex items-center gap-2 mb-1">
    <span>{label}</span>
    {tooltip && (
      <span className="relative group cursor-pointer">
        <Info className="h-4 w-4 text-gray-400" />
        <span className="absolute left-6 top-0 z-10 hidden group-hover:block bg-white border border-gray-300 text-xs text-gray-700 rounded px-2 py-1 shadow-lg w-64">
          {tooltip}
        </span>
      </span>
    )}
  </div>
);

const participationTypes = [
  'One-time',
  'Ongoing',
  'Team-based',
  'Skill-based',
];

const CURRENCY_SYMBOLS = {
  INR: '₹',
  USD: '$',
};

const BudgetCalculatorInputSection: React.FC<Props> = ({ inputs, onInputChange, currency }) => {
  // Validation logic
  const errors: Partial<Record<keyof typeof inputs, string>> = {};
  if (inputs.totalEmployees <= 0) errors.totalEmployees = 'Must be greater than 0';
  if (inputs.participationPercent < 0 || inputs.participationPercent > 100) errors.participationPercent = 'Must be between 0 and 100';
  if (inputs.volunteersPerEvent <= 0) errors.volunteersPerEvent = 'Must be greater than 0';



  return (
    <section className="mb-8 p-4 bg-gray-50 rounded-lg shadow">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
        <span role="img" aria-label="slider">🎛️</span> Employee Engagement & Program Setup
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Total Employees */}
        <div>
          {inputLabel('Total Employees', 'Total number of employees in your company.')}
          <input
            type="range"
            min={0}
            max={100000}
            step={100}
            value={inputs.totalEmployees}
            onChange={e => onInputChange('totalEmployees', Number(e.target.value))}
            className="w-full accent-green-600"
          />
          <div className="text-sm text-gray-700 mt-1">{inputs.totalEmployees.toLocaleString()}</div>
        </div>
        {/* Target Participation % */}
        <div>
          {inputLabel('Target Employee Participation %', 'What % of employees do you want to engage in volunteering?')}
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={inputs.participationPercent}
            onChange={e => onInputChange('participationPercent', Number(e.target.value))}
            className="w-full accent-green-600"
          />
          <div className="text-sm text-gray-700 mt-1">{inputs.participationPercent}%</div>
        </div>
        {/* Avg Volunteer Time */}
        <div>
          {inputLabel('Avg Volunteer Time per Employee (hours)', 'Average number of hours each participating employee will volunteer.')}
          <input
            type="range"
            min={1}
            max={16}
            step={1}
            value={inputs.avgVolunteerTime}
            onChange={e => onInputChange('avgVolunteerTime', Number(e.target.value))}
            className="w-full accent-green-600"
          />
          <div className="text-sm text-gray-700 mt-1">{inputs.avgVolunteerTime} hours</div>
        </div>
        {/* Participation Type */}
        <div>
          {inputLabel('Volunteer Participation Type', 'Choose the primary mode of volunteering participation.')}
          <select
            value={inputs.participationType}
            onChange={e => onInputChange('participationType', e.target.value)}
            className="w-full border border-gray-300 rounded px-2 py-1"
          >
            {participationTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        {/* Volunteering Days Offered */}
        <div>
          {inputLabel('# of Volunteering Days Offered', 'How many days per year are employees offered volunteering opportunities?')}
          <input
            type="range"
            min={1}
            max={12}
            step={1}
            value={inputs.volunteeringDays}
            onChange={e => onInputChange('volunteeringDays', Number(e.target.value))}
            className="w-full accent-green-600"
          />
          <div className="text-sm text-gray-700 mt-1">{inputs.volunteeringDays} days/year</div>
        </div>
        {/* Volunteers per Event */}
        <div>
          {inputLabel('Volunteers per Event', 'How many volunteers do you expect per event?')}
          <input
            type="range"
            min={10}
            max={200}
            step={1}
            value={inputs.volunteersPerEvent}
            onChange={e => onInputChange('volunteersPerEvent', Number(e.target.value))}
            className="w-full accent-green-600"
          />
          <div className="text-sm text-gray-700 mt-1">{inputs.volunteersPerEvent} per event</div>
        </div>
        {/* Days of Service Model */}
        <div>
          {inputLabel('Days of Service Model', 'Is this a dedicated company-wide day of service?')}
          <button
            type="button"
            onClick={() => onInputChange('daysOfService', !inputs.daysOfService)}
            className={`px-4 py-2 rounded ${inputs.daysOfService ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'} transition`}
          >
            {inputs.daysOfService ? 'Yes' : 'No'}
          </button>
        </div>
        {/* Use of Paid Time Off / Volunteer Leave */}
        <div>
          {inputLabel('Use of Paid Time Off / Volunteer Leave?', 'Do employees get paid time off or special leave for volunteering?')}
          <button
            type="button"
            onClick={() => onInputChange('usePaidTimeOff', !inputs.usePaidTimeOff)}
            className={`px-4 py-2 rounded ${inputs.usePaidTimeOff ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'} transition`}
          >
            {inputs.usePaidTimeOff ? 'Yes' : 'No'}
          </button>
        </div>
      </div>
    </section>
  );
};

export default BudgetCalculatorInputSection;
